import { formatNumber } from '@debbie/common/dist/index.js';
import { AggregationType } from '@debbie/common/dist/insights/aggregations.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import { useAggregation } from '@debbie/cortex/dist/insights/index.js';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Errors from '../Errors.js';
import ArrowDownCircle from '../Icons/ArrowDownCircle.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import Table from '../Table.js';
import CaseFilterQueue from './CaseFilterQueue.js';
const AggregationComponent = (props) => {
    const { type, caseFilter } = props;
    const lang = useSelector((s) => s.lang);
    const aggregationLoadable = useAggregation(type, caseFilter);
    const aggregation = h.hasData(aggregationLoadable)
        ? aggregationLoadable.data
        : null;
    const endReasons = useEndReasons();
    const endReasonLookup = h.hasData(endReasons)
        ? objectBy(endReasons.data, (er) => er.id)
        : {};
    return (React.createElement("div", { style: {
            fontSize: '.7em',
            marginBottom: '20px',
            maxHeight: '389px',
            overflowY: 'auto',
        } }, aggregation ? ((() => {
        switch (aggregation.type) {
            case AggregationType.CountAll:
                return (React.createElement("p", { style: {
                        margin: '0',
                        fontSize: '1.3em',
                    } },
                    React.createElement("b", null, typeof aggregation.aggregation.count === 'number'
                        ? formatNumber(aggregation.aggregation.count * 100).slice(0, -3)
                        : '-'),
                    ' ',
                    lang.caseFilter.casesSelected,
                    " ",
                    React.createElement(CaseFilterQueue, null)));
            case AggregationType.ByCaseGroup:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        title: row.label || '-',
                        count: row.count,
                        appendToCaseFilter: {
                            caseGroups: [row.caseGroupId],
                        },
                    })) }));
            case AggregationType.ByCreditor:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        link: `/creditors/${row.creditorId}`,
                        title: row.creditorName,
                        count: row.count,
                        appendToCaseFilter: {
                            creditorIds: [row.creditorId],
                        },
                    })) }));
            case AggregationType.ByWorkflow:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        link: row.workflowId
                            ? `/workflows/${row.workflowId}`
                            : undefined,
                        title: row.workflowTitle || '-',
                        count: row.count,
                        appendToCaseFilter: row.workflowId
                            ? {
                                inWorkflowIds: [row.workflowId],
                            }
                            : undefined,
                    })) }));
            case AggregationType.ByCaseStatus:
                return (React.createElement(React.Fragment, null,
                    h.isError(endReasons) && (React.createElement(Errors, { errors: [lang.errors.loadingError] })),
                    React.createElement(AggregationTable, { data: Object.values(aggregation.aggregation.reduce((acc, row) => {
                            if (!acc[row.status]) {
                                acc[row.status] = {
                                    count: 0,
                                    title: lang.cases[row.status],
                                    appendToCaseFilter: row.status
                                        ? {
                                            caseStatuses: [row.status],
                                        }
                                        : undefined,
                                    sub: [],
                                };
                            }
                            acc[row.status].count += row.count;
                            if (row.endReasonId) {
                                const endReason = lang.endReason[endReasonLookup[row.endReasonId]
                                    ?.handle] ?? endReasonLookup[row.endReasonId]?.label;
                                acc[row.status].sub.push({
                                    id: row.endReasonId,
                                    count: row.count,
                                    title: endReason ?? lang.caseFilter.unknownEndReason,
                                    appendToCaseFilter: {
                                        caseStatuses: [row.status],
                                        caseEndReasonIds: [row.endReasonId],
                                    },
                                });
                            }
                            return acc;
                        }, {})) })));
            case AggregationType.ByBillingModel:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        title: row.billingModelName || '-',
                        count: row.count,
                        appendToCaseFilter: row.billingModelId
                            ? {
                                billingModelIds: [row.billingModelId],
                            }
                            : undefined,
                    })) }));
            case AggregationType.ByBillingAppendixSetup:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        title: row.billingAppendixSetupName || '-',
                        count: row.count,
                        appendToCaseFilter: row.billingAppendixSetupId
                            ? {
                                billingAppendixSetupIds: [row.billingAppendixSetupId],
                            }
                            : undefined,
                    })) }));
            case AggregationType.ByMigratedWorkflowTaskId:
                return (React.createElement(AggregationTable, { data: aggregation.aggregation.map((row) => ({
                        title: row.taskId || '-',
                        count: row.count,
                        appendToCaseFilter: row.taskId
                            ? {
                                alternativeWorkflowPaths: [row.taskId],
                            }
                            : undefined,
                    })) }));
            default:
                return null;
        }
    })()) : h.isError(aggregationLoadable) ? (React.createElement(Errors, { errors: [lang.errors.loadingError] })) : (React.createElement(PlaceholderLoader, null))));
};
export default AggregationComponent;
const AggregationTable = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement(Table, { zebra: true, sumRow: true },
        props.data
            .sort((a, b) => b.count - a.count)
            .map((row) => (React.createElement(AggregationRow, { key: row.link ?? row.appendToCaseFilter?.caseStatuses?.[0] ?? row.title, row: row, rows: props.data }))),
        React.createElement("tr", null,
            React.createElement("td", { style: { paddingLeft: 0 } }, lang.caseFilter.aggregationSum),
            React.createElement("td", { style: { textAlign: 'right', paddingRight: 0 } },
                formatNumber(props.data.reduce((acc, r) => acc + r.count, 0) * 100).slice(0, -3),
                ' ',
                React.createElement(CaseFilterQueue, { appendToCaseFilter: {} })))));
};
const AggregationRow = (props) => {
    const { row, rows } = props;
    const [showSub, setShowSub] = useState(false);
    const hasSub = row.sub && row.sub.length > 0;
    return (React.createElement(React.Fragment, null,
        React.createElement("tr", { key: JSON.stringify(row) },
            React.createElement("td", { style: { paddingLeft: 0, cursor: row.sub ? 'pointer' : 'default' }, "data-tooltip-id": "tooltip-default", "data-tooltip-content": `${((row.count / rows.reduce((acc, r) => acc + r.count, 0)) *
                    100).toFixed(2)} %`, onClick: () => setShowSub(!showSub) }, row.link ? (React.createElement(Link, { to: row.link, style: { color: 'inherit' } },
                React.createElement("div", { title: row.title, style: {
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '150px',
                    } },
                    hasSub && (React.createElement(ArrowDownCircle, { style: {
                            margin: '0.2em 0 -0.2em',
                            transform: `rotate(${showSub ? '0deg' : '-90deg'})`,
                        } })),
                    ' ',
                    row.title))) : (React.createElement("div", { title: row.title, style: {
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    maxWidth: '150px',
                } },
                hasSub && (React.createElement(ArrowDownCircle, { style: {
                        margin: '0.2em 0 -0.2em',
                        transform: `rotate(${showSub ? '0deg' : '-90deg'})`,
                    } })),
                ' ',
                row.title))),
            React.createElement("td", { style: { textAlign: 'right', paddingRight: 0 } },
                formatNumber(row.count * 100).slice(0, -3),
                ' ',
                row.appendToCaseFilter && (React.createElement(CaseFilterQueue, { appendToCaseFilter: row.appendToCaseFilter })))),
        showSub &&
            hasSub &&
            row.sub
                ?.sort((a, b) => b.count - a.count)
                .map((sub) => (React.createElement("tr", { key: sub.id, style: { fontSize: '0.8em' } },
                React.createElement("td", { style: { paddingLeft: 0 }, "data-tooltip-id": "tooltip-default" }, sub.link ? (React.createElement(Link, { to: sub.link, style: { color: 'inherit' } },
                    React.createElement("div", { title: sub.title, style: {
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                        } }, sub.title))) : (React.createElement("div", { title: sub.title, style: {
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        maxWidth: '150px',
                    } }, sub.title))),
                React.createElement("td", { style: { textAlign: 'right', paddingRight: 0 } },
                    formatNumber(sub.count * 100).slice(0, -3),
                    ' ',
                    row.appendToCaseFilter && (React.createElement(CaseFilterQueue, { appendToCaseFilter: sub.appendToCaseFilter }))))))));
};
//# sourceMappingURL=Aggregation.js.map